import type { StRoomPoolListItem, StRoomPoolMember } from '../../../services/socialTradingService';

/** True when volume or profit reward is a number strictly greater than 0. */
export function hasPositiveRewardAmount(volumeAmount: string, profitAmount: string): boolean {
  const volume = Number(volumeAmount);
  const profit = Number(profitAmount);
  return (Number.isFinite(volume) && volume > 0) || (Number.isFinite(profit) && profit > 0);
}

/** USDC precision. Upstream rejects anything longer instead of rounding it. */
export const POOL_AMOUNT_MAX_DECIMALS = 6;

const AMOUNT = /^\d+(\.\d+)?$/;

export type PoolAmountError = 'invalid' | 'precision';

/**
 * Why an amount field cannot be saved, or undefined. An empty value is left to
 * the caller: a blank target means something different from a blank threshold.
 */
export function poolAmountError(value: string): PoolAmountError | undefined {
  const raw = value.trim();
  if (!raw) return undefined;
  if (!AMOUNT.test(raw)) return 'invalid';
  const decimals = raw.split('.')[1]?.length ?? 0;
  return decimals > POOL_AMOUNT_MAX_DECIMALS ? 'precision' : undefined;
}

const isPositive = (value: string): boolean => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0;
};

/** The six V2.5 fields the form owns, alongside the amounts it already had. */
export type PoolDualFields = {
  volumeEnabled: boolean;
  profitEnabled: boolean;
  requirePersonalVolume: boolean;
  requirePersonalProfit: boolean;
  minPersonalVolume: string;
  minPersonalProfit: string;
};

/**
 * The dual-pool half of the edit form, read from a room-pool row.
 *
 * A room with no cycle (`poolId` null) reports all four booleans as `false` —
 * Go zero values, not a choice anyone made. Binding those back would save
 * `volumeEnabled: false, profitEnabled: false` and be rejected with
 * POOL_SOURCE_REQUIRED, so such a room starts with both pools on instead.
 *
 * A null threshold means the cycle predates the feature and still reads the
 * global value; it stays blank here so the placeholder can show the global one
 * and saving re-pins it.
 */
export function poolFormFromItem(item: StRoomPoolListItem): PoolDualFields {
  const hasCycle = Boolean(item.poolId);
  return {
    volumeEnabled: hasCycle ? item.volumeEnabled !== false : true,
    profitEnabled: hasCycle ? item.profitEnabled !== false : true,
    requirePersonalVolume: hasCycle ? item.requirePersonalVolume !== false : true,
    requirePersonalProfit: hasCycle ? item.requirePersonalProfit !== false : true,
    minPersonalVolume: item.minPersonalVolume ?? '',
    minPersonalProfit: item.minPersonalProfit ?? '',
  };
}

export type PoolFormValues = PoolDualFields & {
  volumeAmount: string;
  profitAmount: string;
  volumeTarget: string;
  profitTarget: string;
};

export type PoolFormError =
  | { field: 'form'; code: 'sourceRequired' }
  | { field: keyof PoolFormValues; code: PoolAmountError | 'targetRequired' };

/**
 * The first reason the form cannot be saved, or undefined.
 *
 * Each case mirrors an upstream rejection, so catching it here saves a round
 * trip. A disabled pool is not validated: its amounts are ignored on settlement.
 */
export function validatePoolForm(values: PoolFormValues): PoolFormError | undefined {
  if (!values.volumeEnabled && !values.profitEnabled) {
    return { field: 'form', code: 'sourceRequired' };
  }

  const fields: (keyof PoolFormValues)[] = [
    'volumeAmount',
    'profitAmount',
    'volumeTarget',
    'profitTarget',
    'minPersonalVolume',
    'minPersonalProfit',
  ];
  const bad = fields
    .map((field) => ({ field, code: poolAmountError(values[field] as string) }))
    .find((entry) => entry.code);
  if (bad?.code) return { field: bad.field, code: bad.code };

  // A pool with a reward but no unlock target can never pay out.
  if (values.volumeEnabled && isPositive(values.volumeAmount) && !isPositive(values.volumeTarget)) {
    return { field: 'volumeTarget', code: 'targetRequired' };
  }
  if (values.profitEnabled && isPositive(values.profitAmount) && !isPositive(values.profitTarget)) {
    return { field: 'profitTarget', code: 'targetRequired' };
  }

  return undefined;
}

/**
 * `configurePool` OVERWRITES: an omitted field falls back to its default, not
 * to the stored value — omitting `volumeTarget` resets it to 200000. So every
 * field the form owns is sent on every save.
 *
 * The two personal thresholds are the exception: blank is meaningful there, and
 * omitting them pins the current global value, which is what blank means in the
 * form.
 */
export function toConfigurePoolBody(
  values: PoolFormValues & { roomId: string; cycleStart: string; cycleEnd: string; cycleLabel?: string },
): Record<string, unknown> {
  const minPersonalVolume = values.minPersonalVolume.trim();
  const minPersonalProfit = values.minPersonalProfit.trim();

  return {
    roomId: values.roomId,
    poolEnabled: true,
    cycleStart: values.cycleStart,
    cycleEnd: values.cycleEnd,
    ...(values.cycleLabel ? { cycleLabel: values.cycleLabel } : {}),
    volumeAmount: values.volumeAmount.trim() || '0',
    profitAmount: values.profitAmount.trim() || '0',
    volumeTarget: values.volumeTarget.trim() || '0',
    profitTarget: values.profitTarget.trim() || '0',
    volumeEnabled: values.volumeEnabled,
    profitEnabled: values.profitEnabled,
    requirePersonalVolume: values.requirePersonalVolume,
    requirePersonalProfit: values.requirePersonalProfit,
    ...(minPersonalVolume ? { minPersonalVolume } : {}),
    ...(minPersonalProfit ? { minPersonalProfit } : {}),
  };
}

/**
 * V2.5 item 5: which field a save failure belongs to, and what to say about it.
 *
 * Read from `upstreamCode` — `code` stays `SOCIAL_TRADING_UPSTREAM_ERROR` for
 * every one of these, and parsing `message` would be guesswork. An unmapped
 * code returns undefined so the caller falls back to the server's own message.
 */
const SAVE_ERROR_FIELDS: Record<string, { field: keyof PoolFormValues | 'form' | 'cycleStart' | 'cycleEnd' | 'cycleLabel'; key: string }> = {
  POOL_VOLUME_AMOUNT_NEGATIVE: { field: 'volumeAmount', key: 'amountNegative' },
  POOL_VOLUME_AMOUNT_PRECISION: { field: 'volumeAmount', key: 'precision' },
  POOL_PROFIT_AMOUNT_NEGATIVE: { field: 'profitAmount', key: 'amountNegative' },
  POOL_PROFIT_AMOUNT_PRECISION: { field: 'profitAmount', key: 'precision' },
  POOL_VOLUME_TARGET_NEGATIVE: { field: 'volumeTarget', key: 'amountNegative' },
  POOL_VOLUME_TARGET_PRECISION: { field: 'volumeTarget', key: 'precision' },
  POOL_VOLUME_TARGET_REQUIRED: { field: 'volumeTarget', key: 'targetRequired' },
  POOL_PROFIT_TARGET_NEGATIVE: { field: 'profitTarget', key: 'amountNegative' },
  POOL_PROFIT_TARGET_PRECISION: { field: 'profitTarget', key: 'precision' },
  POOL_PROFIT_TARGET_REQUIRED: { field: 'profitTarget', key: 'targetRequired' },
  POOL_MIN_PERSONAL_VOLUME_NEGATIVE: { field: 'minPersonalVolume', key: 'amountNegative' },
  POOL_MIN_PERSONAL_VOLUME_PRECISION: { field: 'minPersonalVolume', key: 'precision' },
  POOL_MIN_PERSONAL_PROFIT_NEGATIVE: { field: 'minPersonalProfit', key: 'amountNegative' },
  POOL_MIN_PERSONAL_PROFIT_PRECISION: { field: 'minPersonalProfit', key: 'precision' },
  POOL_CYCLE_START_REQUIRED: { field: 'cycleStart', key: 'cycleStartRequired' },
  POOL_CYCLE_END_REQUIRED: { field: 'cycleEnd', key: 'cycleEndRequired' },
  POOL_CYCLE_END_BEFORE_START: { field: 'cycleEnd', key: 'cycleEndBeforeStart' },
  POOL_CYCLE_LABEL_LANG_INVALID: { field: 'cycleLabel', key: 'cycleLabelLangInvalid' },
  POOL_SOURCE_REQUIRED: { field: 'form', key: 'sourceRequired' },
};

export type PoolSaveError = { field: string; messageKey: string };

export function poolSaveError(upstreamCode?: string): PoolSaveError | undefined {
  const hit = upstreamCode ? SAVE_ERROR_FIELDS[upstreamCode] : undefined;
  return hit ? { field: hit.field, messageKey: `module.socialTrading.poolErrors.${hit.key}` } : undefined;
}

/**
 * The per-source breakdown of a member's payout, or null when there is none to
 * show.
 *
 * A cycle settled before V2.5 returns an empty `rewardSources`, and its whole
 * payout sits in `estReward` — rendering "no sources" there would read as a
 * zero reward, so the caller falls back to the single total instead.
 */
export function memberRewardSources(member: StRoomPoolMember) {
  const sources = member.rewardSources ?? [];
  return sources.length > 0 ? sources : null;
}

/**
 * `"VOLUME"` → `module.socialTrading.rewardSourceKind.VOLUME`. The upstream
 * field is a String rather than an enum, so an unknown kind has to survive as
 * its own label instead of rendering blank.
 */
export const rewardSourceKindKey = (kind: string): string =>
  `module.socialTrading.rewardSourceKind.${kind}`;
